/**
 * @description A component that fades in and fades out
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import Box from '@mui/material/Box';

const styles = {
  container: {
    transition: 'opacity 0.75s ease'
  }
};

/**
 * @prop children {React.Element | React.Element[]} - react element(s) to fade in and out
 * @prop onFinish (function) - callback function when the fade-in/fade-out animation is complete
 * @prop onShow {function} - callback function when the child elements have finished fading in
 * @prop waitInterval {number} - duration to wait between fading-in and fading-out (in ms)
 */
const FadeInFadeOut = ({
  children,
  onFinish,
  onShow,
  waitInterval,
  sx
}) => {
  const [show, setShow] = React.useState(false);

  /**
   * Callback function that is called when either the fade-in or fade-out animation is finished
   */
  function handleTransitionEnd () {
    // If the fade-in animation is complete, then wait an arbitrary amount of time and trigger the fade-out animation
    // If the fade-out animation is complete, then call the onFinish callback
    if (show) {
      setTimeout(() => setShow(false), waitInterval);
    } else {
      onFinish?.();
    }
  }

  /**
   * Fade the toast message into view on render
   */
  React.useEffect(() => {
    if (show === true) {
      return;
    }

    // For some strange reason, updating the show state in a react render cycle sometimes causes a race condition where
    // the animation is skipped.  Wrapping it in a setTimeout seems to fix that.
    setTimeout(() => {
      setShow(true);
    }, 10);

    // Wait for the fade-in animation to finish and then call the onShow callback, if it exists.
    // We use a setTimeout instead of onTransitionEnd is because its been observed that sometimes the animation doesn't trigger.
    setTimeout(() => {
      onShow?.();
    }, 750);
  }, []);

  return (
    <Box sx={{ ...styles.container, ...sx }} style={{ opacity: show ? 1 : 0 }} onTransitionEnd={() => handleTransitionEnd()}>
      {children}
    </Box>
  );
};

FadeInFadeOut.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired,
  onShow: PropTypes.func,
  onFinish: PropTypes.func,
  waitInterval: PropTypes.number,
  sx: PropTypes.object
};

FadeInFadeOut.defaultProps = {
  onFinish: null,
  onShow: null,
  waitInterval: 5000,
  sx: null
}

export default FadeInFadeOut;
